import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker } from 'react-leaflet';
import PotenzialPopup from '../components/PotenzialPopup';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { Kunde, PotenzialKunde } from '../types';
import { kundeService } from '../services/kundeService';
import { potenzialService } from '../services/potenzialService';
import toast from 'react-hot-toast';
import { Icon, PageHeader, Spinner, filterAktiv, fehlerText } from '../components/ui';

// Fix default marker icon (webpack asset issue with leaflet)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom brand-colored marker
const createBrandMarker = () =>
  new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
    className: 'leaflet-marker-brand',
  });

const createGrayMarker = () =>
  new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
    className: 'leaflet-marker-gray',
  });

const brandMarker = createBrandMarker();
const grayMarker = createGrayMarker();

// Component to fit map bounds to visible markers
const FitBounds: React.FC<{ kunden: Kunde[] }> = ({ kunden }) => {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (fitted.current) return;
    const withCoords = kunden.filter(k => k.lat != null && k.lng != null);
    if (withCoords.length === 0) return;
    const bounds = L.latLngBounds(withCoords.map(k => [k.lat!, k.lng!]));
    map.fitBounds(bounds, { padding: [40, 40] });
    fitted.current = true;
  }, [kunden, map]);

  return null;
};

const Karte: React.FC = () => {
  const navigate = useNavigate();
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPins, setShowPins] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [satellite, setSatellite] = useState(false);
  const [geocodingId, setGeocodingId] = useState<number | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
  const batchCancelRef = useRef(false);
  const [search, setSearch] = useState('');
  const [potenziale, setPotenziale] = useState<PotenzialKunde[]>([]);
  const [showPotenziale, setShowPotenziale] = useState(false);
  const [, setDeletingPotenzialId] = useState<number | null>(null);

  useEffect(() => {
    kundeService.getAll()
      .then(setKunden)
      .catch((err) => toast.error(fehlerText(err, 'Kunden konnten nicht geladen werden')))
      .finally(() => setLoading(false));
    potenzialService.getAll().then(setPotenziale).catch(() => {});
  }, []);

  const kundenMitKoord = kunden.filter(k => k.lat != null && k.lng != null);
  const kundenOhneKoord = kunden.filter(k => k.lat == null || k.lng == null);

  const filteredKunden = kunden.filter(k => {
    const q = search.toLowerCase();
    return (
      k.nachnameFirma.toLowerCase().includes(q) ||
      k.vorname?.toLowerCase().includes(q) ||
      k.ort?.toLowerCase().includes(q) ||
      k.plz?.toLowerCase().includes(q)
    );
  });

  const activeMarkers = showPins
    ? filteredKunden.filter(k => k.lat != null && k.lng != null && k.active)
    : [];
  const inactiveMarkers = showPins && showInactive
    ? filteredKunden.filter(k => k.lat != null && k.lng != null && !k.active)
    : [];
  const visibleMarkers = [...activeMarkers, ...inactiveMarkers];

  const handleGeocodeAll = async () => {
    const candidates = kunden.filter(
      k => k.lat == null && !!(k.strasse && k.plz && k.ort)
    );
    if (candidates.length === 0) {
      toast('Alle Kunden mit vollständiger Adresse sind bereits verortet.');
      return;
    }

    setBatchRunning(true);
    batchCancelRef.current = false;
    setBatchProgress({ done: 0, total: candidates.length });

    let done = 0;
    for (const kunde of candidates) {
      if (batchCancelRef.current) break;

      try {
        const updated = await kundeService.geocode(kunde.id);
        setKunden(prev => prev.map(k => k.id === updated.id ? updated : k));
      } catch {
        // silently skip
      }

      done++;
      setBatchProgress({ done, total: candidates.length });

      // Nominatim rate limit: max 1 req/s
      if (done < candidates.length && !batchCancelRef.current) {
        await new Promise(r => setTimeout(r, 1100));
      }
    }

    setBatchRunning(false);
    setBatchProgress(null);
    if (!batchCancelRef.current) {
      toast.success('Alle Kunden verortet');
    }
  };

  const handleGeocode = async (kunde: Kunde) => {
    setGeocodingId(kunde.id);
    try {
      const updated = await kundeService.geocode(kunde.id);
      setKunden(prev => prev.map(k => k.id === updated.id ? updated : k));
      toast.success('Koordinaten ermittelt');
    } catch (e) {
      toast.error(fehlerText(e, 'Koordinaten konnten nicht ermittelt werden'));
    } finally {
      setGeocodingId(null);
    }
  };

  return (
    <div className="p-4 md:p-6 flex flex-col gap-4 h-full">
      {/* Header */}
      <PageHeader
        className="!mb-0"
        title="Karte"
        subtitle={`${kundenMitKoord.length} von ${kunden.length} Kunden verortet · ${kunden.filter(k => k.active).length} aktiv`}
        actions={<>
          <div className="relative">
            <Icon.Search className={`pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 ${search ? 'text-brand-600' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Kunden suchen …"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={`border border-gray-300 rounded-lg pl-8 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 w-52 ${search ? filterAktiv : ''}`}
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} aria-label="Suche zurücksetzen" title="Suche zurücksetzen"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-700 hover:text-brand-900">
                <Icon.Close className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex rounded-lg overflow-hidden border border-gray-300 text-sm font-medium">
            <button
              onClick={() => setSatellite(false)}
              className={`px-3 py-2 transition-colors ${!satellite ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              <span className="inline-flex items-center gap-1.5"><Icon.Karte className="h-4 w-4" />Karte</span>
            </button>
            <button
              onClick={() => setSatellite(true)}
              className={`px-3 py-2 transition-colors border-l border-gray-300 ${satellite ? 'bg-brand-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              <span className="inline-flex items-center gap-1.5"><Icon.Bild className="h-4 w-4" />Satellit</span>
            </button>
          </div>
          <button
            onClick={() => setShowPins(p => !p)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showPins
                ? 'bg-brand-700 text-white hover:bg-brand-800'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <span className="inline-flex items-center gap-1.5"><Icon.MapPin className="h-4 w-4" />{showPins ? 'Pins an' : 'Pins aus'}</span>
          </button>
          <button
            onClick={() => setShowInactive(p => !p)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showInactive
                ? 'bg-gray-500 text-white hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            title="Inaktive Kunden anzeigen (grauer Pin)"
          >
            Inaktive {showInactive ? 'ein' : 'aus'}
          </button>
          {potenziale.length > 0 && (
            <button
              onClick={() => setShowPotenziale(p => !p)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showPotenziale
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title={`${potenziale.length} Potenziale`}
            >
              Potenziale {showPotenziale ? 'ein' : 'aus'}
            </button>
          )}
        </>}
      />

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: '500px' }}>
        {loading ? (
          <div className="flex items-center justify-center h-full bg-gray-50 text-brand-600">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <MapContainer
            center={[51.1657, 10.4515]}
            zoom={6}
            maxZoom={22}
            style={{ height: '100%', width: '100%' }}
          >
            {satellite ? (
              <TileLayer
                attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url={`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`}
                tileSize={512}
                zoomOffset={-1}
                maxNativeZoom={20}
                maxZoom={22}
              />
            ) : (
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxNativeZoom={19}
                maxZoom={22}
              />
            )}
            <FitBounds kunden={kundenMitKoord} />
            {showPotenziale && potenziale.filter(p => p.lat && p.lon).map(p => (
              <CircleMarker
                key={p.id}
                center={[Number(p.lat), Number(p.lon)]}
                radius={5}
                pathOptions={{ fillColor: p.color ?? '#27ae60', color: '#fff', weight: 1, fillOpacity: 0.8 }}
              >
                <PotenzialPopup
                  item={p}
                  onDelete={async (id) => {
                    setDeletingPotenzialId(id);
                    try {
                      await potenzialService.delete(id);
                      setPotenziale(prev => prev.filter(x => x.id !== id));
                      toast.success('Potenzial gelöscht');
                    } catch (err) {
                      toast.error(fehlerText(err, 'Potenzial konnte nicht gelöscht werden'));
                    } finally {
                      setDeletingPotenzialId(null);
                    }
                  }}
                />
              </CircleMarker>
            ))}
            {visibleMarkers.map(kunde => (
              <Marker
                key={kunde.id}
                position={[kunde.lat!, kunde.lng!]}
                icon={kunde.active ? brandMarker : grayMarker}
              >
                <Popup minWidth={220}>
                  <div className="text-sm">
                    <div className="font-semibold text-gray-900 text-base mb-1">
                      {kunde.vorname ? `${kunde.vorname} ${kunde.nachnameFirma}` : kunde.nachnameFirma}
                    </div>
                    {(kunde.strasse || kunde.plz || kunde.ort) && (
                      <div className="text-gray-600 mb-2">
                        {kunde.strasse && <div>{kunde.strasse}</div>}
                        {(kunde.plz || kunde.ort) && (
                          <div>{[kunde.plz, kunde.ort].filter(Boolean).join(' ')}</div>
                        )}
                      </div>
                    )}
                    {kunde.mobiltelefon && (
                      <div className="text-gray-600 mb-1 flex items-center gap-1.5">
                        <Icon.Telefon className="h-3.5 w-3.5 flex-shrink-0" /><a href={`tel:${kunde.mobiltelefon}`} className="hover:underline">{kunde.mobiltelefon}</a>
                      </div>
                    )}
                    {kunde.email && (
                      <div className="text-gray-600 mb-2 flex items-center gap-1.5">
                        <Icon.Mail className="h-3.5 w-3.5 flex-shrink-0" /><a href={`mailto:${kunde.email}`} className="hover:underline">{kunde.email}</a>
                      </div>
                    )}
                    {kunde.entfernungKm != null && kunde.entfernungKm > 0 && (
                      <div className="text-gray-500 text-xs mb-2 flex items-center gap-1.5"><Icon.Auto className="h-3.5 w-3.5" />{kunde.entfernungKm} km</div>
                    )}
                    <button
                      onClick={() => navigate(`/kunden/${kunde.id}`)}
                      className="mt-1 w-full text-center bg-brand-700 text-white text-xs px-3 py-1.5 rounded hover:bg-brand-800 transition-colors"
                    >
                      Stammdaten öffnen
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>

      {/* Kunden ohne Koordinaten */}
      {kundenOhneKoord.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-700">
              Kunden ohne Koordinaten ({kundenOhneKoord.length})
            </h2>
            <div className="flex items-center gap-3">
              {batchProgress && (
                <span className="text-xs text-gray-500">
                  {batchProgress.done} / {batchProgress.total}
                </span>
              )}
              {batchRunning ? (
                <button
                  onClick={() => { batchCancelRef.current = true; }}
                  className="text-xs bg-red-500 text-white px-3 py-1.5 rounded hover:bg-red-600 transition-colors"
                >
                  Abbrechen
                </button>
              ) : (
                <button
                  onClick={handleGeocodeAll}
                  className="text-xs bg-brand-600 text-white px-3 py-1.5 rounded hover:bg-brand-700 transition-colors"
                >
                  Alle orten
                </button>
              )}
            </div>
          </div>
          <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
            {kundenOhneKoord.map(kunde => {
              const hasAddress = !!(kunde.strasse && kunde.plz && kunde.ort);
              return (
                <div key={kunde.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      {kunde.vorname ? `${kunde.vorname} ${kunde.nachnameFirma}` : kunde.nachnameFirma}
                    </div>
                    <div className="text-xs text-gray-400">
                      {hasAddress
                        ? `${kunde.strasse}, ${kunde.plz} ${kunde.ort}`
                        : 'Adresse unvollständig'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasAddress && (
                      <button
                        onClick={() => handleGeocode(kunde)}
                        disabled={geocodingId === kunde.id}
                        className="text-xs bg-brand-600 text-white px-2.5 py-1 rounded hover:bg-brand-700 disabled:opacity-50 transition-colors"
                      >
                        {geocodingId === kunde.id ? <Spinner className="h-3.5 w-3.5" /> : 'Orten'}
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/kunden/${kunde.id}`)}
                      className="text-xs text-brand-600 hover:text-brand-800 hover:underline"
                    >
                      Bearbeiten
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Karte;
